import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import api from '@/lib/axios';
import { formatScheduleLabel } from '@/lib/utils';
import { getFooterSettings, footerToParams } from '@/components/FooterDateBar';

const Section = ({ title, children }) => (
  <>
    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 border-t border-slate-100 first:border-t-0">{title}</div>
    <ul>{children}</ul>
  </>
);

const Item = ({ onClick, testId, title, subtitle, hover = 'hover:bg-blue-50' }) => (
  <li
    onClick={onClick}
    className={`px-3 py-2 ${hover} cursor-pointer border-b border-slate-100 last:border-0`}
    data-testid={testId}
  >
    <div className="font-medium text-sm text-slate-800">{title}</div>
    <div className="text-xs text-slate-500">{subtitle}</div>
  </li>
);

const asArr = (r) => (Array.isArray(r?.data) ? r.data : r?.data?.items || []);

/**
 * Debounced live-search dropdown: Schüler, Zahlungspflichtige, Lehrkräfte, Veranstaltungen.
 * Treffer berücksichtigen die Fußleisten-Filter (Zeitraum / Nur Aktive / Aktiv ab).
 * Enter (via ref.acceptAll) lädt alle aktuellen Treffer zum Durchsteppen.
 */
export const LiveSearchDropdown = forwardRef(function LiveSearchDropdown(
  { searchTerm, onSelect, onSelectCourse, onSelectTeacher, onSelectGuardian, onAcceptAll, isOpen, onClose, inline = false },
  ref,
) {
  const [studentResults, setStudentResults] = useState([]);
  const [courseResults, setCourseResults] = useState([]);
  const [teacherResults, setTeacherResults] = useState([]);
  const [guardianResults, setGuardianResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [footerTick, setFooterTick] = useState(0);
  const dropdownRef = useRef(null);
  const resultsRef = useRef({ students: [], guardians: [], teachers: [], courses: [] });

  useEffect(() => {
    resultsRef.current = {
      students: studentResults,
      guardians: guardianResults,
      teachers: teacherResults,
      courses: courseResults,
    };
  }, [studentResults, guardianResults, teacherResults, courseResults]);

  useEffect(() => {
    const h = () => setFooterTick((n) => n + 1);
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  useImperativeHandle(ref, () => ({
    acceptAll: () => {
      const r = resultsRef.current;
      const hasAny = r.students.length || r.guardians.length || r.teachers.length || r.courses.length;
      if (!hasAny) return false;
      onAcceptAll?.(r);
      return true;
    },
    isLoading: () => loading,
  }), [onAcceptAll, loading]);

  useEffect(() => {
    let cancelled = false;
    const runSearch = async () => {
      if (searchTerm.length < 2) {
        setStudentResults([]); setCourseResults([]); setTeacherResults([]); setGuardianResults([]);
        return;
      }
      setLoading(true);
      try {
        const footer = footerToParams(getFooterSettings());
        const [enrRes, teachEnrRes, tRes, gRes] = await Promise.all([
          api.get('/enrollments', {
            params: { search: searchTerm, page: 1, per_page: 40, ...footer },
          }).catch(() => ({ data: { items: [] } })),
          api.get('/enrollments', {
            params: { q_lehrkraft: searchTerm, page: 1, per_page: 40, ...footer },
          }).catch(() => ({ data: { items: [] } })),
          api.get('/teachers', { params: { search: searchTerm, limit: 20 } }).catch(() => ({ data: [] })),
          api.get('/guardians', { params: { search: searchTerm, limit: 20 } }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const enrItems = asArr(enrRes);
        const teachEnrItems = asArr(teachEnrRes);

        // Schüler einzigartig aus gefilterten Belegungen
        const studentsById = new Map();
        for (const e of enrItems) {
          const s = e.student;
          if (!s?.id || studentsById.has(s.id)) continue;
          studentsById.set(s.id, {
            id: s.id,
            first_name: s.first_name,
            last_name: s.last_name,
            email: s.email,
            phone: s.phone,
          });
        }
        setStudentResults([...studentsById.values()].slice(0, 20));

        // Veranstaltungen einzigartig
        const coursesById = new Map();
        for (const e of enrItems) {
          const c = e.course;
          if (!c?.id || coursesById.has(c.id)) continue;
          coursesById.set(c.id, {
            id: c.id,
            name: c.name,
            teacher_name: c.teacher_name,
          });
        }
        setCourseResults([...coursesById.values()].slice(0, 20));

        // Lehrkräfte: Namenssuche ∩ Belegungen im Fußleisten-Filter
        const teacherNames = new Set(
          teachEnrItems
            .map((e) => (e.course?.teacher_name || '').trim())
            .filter(Boolean),
        );
        const teachers = asArr(tRes).filter((t) => {
          const label = `${t.last_name || ''}, ${t.first_name || ''}`.replace(/^,\s*/, '').trim();
          return teacherNames.has(label);
        });
        setTeacherResults(teachers.slice(0, 20));

        // Zahler: Namenssuche ∩ mindestens eine Belegung im Fußleisten-Filter
        const guardians = asArr(gRes).slice(0, 12);
        if (!guardians.length) {
          setGuardianResults([]);
        } else {
          const checks = await Promise.all(
            guardians.map(async (g) => {
              try {
                const r = await api.get('/enrollments', {
                  params: { guardian_id: g.id, page: 1, per_page: 1, ...footer },
                });
                return (r.data?.total ?? 0) > 0 ? g : null;
              } catch {
                return null;
              }
            }),
          );
          if (cancelled) return;
          setGuardianResults(checks.filter(Boolean));
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const debounce = setTimeout(runSearch, 300);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [searchTerm, footerTick]);

  useEffect(() => {
    if (inline) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      // Fußleisten-Filter ändern → Dropdown offen lassen und neu laden
      if (event.target.closest?.('[data-testid="uv-footer-bar"]')) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, inline]);

  if (!isOpen || searchTerm.length < 2) return null;

  const empty = !studentResults.length && !courseResults.length && !teacherResults.length && !guardianResults.length;

  return (
    <div
      ref={dropdownRef}
      className={inline
        ? 'bg-white max-h-96 overflow-y-auto'
        : 'absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto'}
      data-testid="live-search-dropdown"
    >
      {loading ? (
        <div className="p-3 text-center text-sm text-slate-500">Suche...</div>
      ) : empty ? (
        <div className="p-3 text-center text-sm text-slate-500">Keine Ergebnisse (mit Fußleisten-Filter)</div>
      ) : (
        <div>
          {studentResults.length > 0 && (
            <Section title="Schüler:innen">
              {studentResults.map((s) => (
                <Item key={s.id} onClick={() => onSelect(s)} testId={`live-search-result-${s.id}`}
                  title={`${s.last_name}, ${s.first_name}`}
                  subtitle={s.email || s.phone || 'Keine Kontaktdaten'} />
              ))}
            </Section>
          )}
          {guardianResults.length > 0 && (
            <Section title="Zahlungspflichtige">
              {guardianResults.map((g) => (
                <Item key={g.id} onClick={() => onSelectGuardian?.(g)} testId={`live-search-guardian-${g.id}`}
                  title={`${g.last_name}, ${g.first_name}`}
                  subtitle={g.email || g.phone || 'Zahlungspflichtige:r'} hover="hover:bg-brand-red/5" />
              ))}
            </Section>
          )}
          {teacherResults.length > 0 && (
            <Section title="Lehrkräfte">
              {teacherResults.map((t) => (
                <Item key={t.id} onClick={() => onSelectTeacher?.(t)} testId={`live-search-teacher-${t.id}`}
                  title={`${t.last_name}, ${t.first_name}`}
                  subtitle={t.email || 'Lehrkraft'} hover="hover:bg-emerald-50" />
              ))}
            </Section>
          )}
          {courseResults.length > 0 && (
            <Section title="Veranstaltungen">
              {courseResults.map((c) => (
                <Item key={c.id} onClick={() => onSelectCourse?.(c)} testId={`live-search-course-${c.id}`}
                  title={formatScheduleLabel(c.name) || c.name}
                  subtitle={[c.teacher_name, c.enrollment_count != null ? `${c.enrollment_count} Belegung(en)` : null].filter(Boolean).join(' · ') || 'Veranstaltung'}
                  hover="hover:bg-brand-red/5" />
              ))}
            </Section>
          )}
          <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50">
            Enter: alle Treffer laden und durchsteppen · gefiltert nach Fußleiste
          </div>
        </div>
      )}
    </div>
  );
});

export default LiveSearchDropdown;
